import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { PITCH_USAGE_TYPE_OPTIONS, type PitchUsageType } from "@/lib/pitchUsageType";
import { PitchFeatureSelect } from "@/components/pitches/PitchFeatureSelect";
import { usePitchFeatures } from "@/lib/usePitchFeatures";
import type { PitchCategory } from "@/types/camping";

const EMPTY_FORM = {
  code: "",
  label: "",
  category_id: "",
  size_sqm: "",
  usage_type: "tourism" as PitchUsageType,
  has_electricity: false,
  has_water: false,
  has_sewage: false,
  feature_ids: [] as string[],
};

export interface CreatePitchModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  categories: PitchCategory[];
}

export function CreatePitchModal({
  open,
  onClose,
  onCreated,
  categories,
}: CreatePitchModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { features } = usePitchFeatures(open);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setSubmitting(false);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/pitches", {
        code: form.code.trim(),
        label: form.label.trim() || null,
        category_id: form.category_id || null,
        size_sqm: form.size_sqm ? Number(form.size_sqm) : null,
        usage_type: form.usage_type,
        has_electricity: form.has_electricity,
        has_water: form.has_water,
        has_sewage: form.has_sewage,
        feature_ids: form.feature_ids,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neuer Stellplatz"
      description="Stellplatz im Bestand anlegen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Code *</span>
            <input
              className="eh-input"
              placeholder="z. B. A-12"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              required
              autoFocus
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Bezeichnung</span>
            <input
              className="eh-input"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Kategorie</span>
            <select
              className="eh-select-input"
              value={form.category_id}
              onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
            >
              <option value="">Platzkategorie wählen…</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Platztyp</span>
            <select
              className="eh-select-input"
              value={form.usage_type}
              onChange={(e) =>
                setForm((f) => ({ ...f, usage_type: e.target.value as PitchUsageType }))
              }
            >
              {PITCH_USAGE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-label-secondary">Größe m²</span>
            <input
              className="eh-input"
              value={form.size_sqm}
              onChange={(e) => setForm((f) => ({ ...f, size_sqm: e.target.value }))}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-4 text-sm text-label">
          {(
            [
              ["has_electricity", "Strom"],
              ["has_water", "Wasser"],
              ["has_sewage", "Abwasser"],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>
        {features.length > 0 ? (
          <div className="space-y-1">
            <span className="block text-sm text-label-secondary">Eigenschaften</span>
            <PitchFeatureSelect
              features={features}
              selectedIds={form.feature_ids}
              onChange={(feature_ids) => setForm((f) => ({ ...f, feature_ids }))}
            />
          </div>
        ) : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Platz anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
